// The monthly summary — one loader, three surfaces.
//
// Replaces a spreadsheet the studio maintains by hand: per person, per month,
// hours worked against hours owed, with the leave that explains the difference.
//
// It relies on RLS to scope rows rather than filtering by person: every table
// below has a `profile_id = auth.uid() or has_perm(...)` policy, so the SAME code
// returns the whole org for HR and exactly one row for a member. There is no
// `profileIds` parameter and there must not be one — a client-side filter would
// be a second, weaker answer to a question Postgres already answers.
//
// ⚠️ The permission asymmetry that makes the org-wide gate load-bearing:
//   attendance          → people:VIEW
//   leave_requests      → people:FULL
//   leave_day_exceptions→ people:FULL
//   overtime_ledger     → people:FULL
//   overtime_settlement → people:FULL
//   time_entries        → timesheets:FULL   ← a different module entirely
//   compensated_hours   → people:EDIT, own, or the subject's MANAGER
// A `lead` holds people:view + timesheets:full, so they would receive everyone's
// HOURS but only their OWN leave — and every colleague's required hours would come
// back too high, confidently and silently. compensated_hours adds a second shape
// of the same trap: a manager sees their reporting line's credit and nobody
// else's, so their colleagues' effective hours would read low. PostgREST returns an empty set, not a
// 403, so there is nothing to catch at runtime. Callers must gate on
// `canSeeOrgSummary()` below.
import { supabase } from './supabase';
import { orgDateKey } from './dates';
import { personDay, type OrgHours } from './orgHours';
import { can, type PermMap } from './roles';
import type { Workdays } from './workdays';
import {
  chargedLeaveByDay,
  completionPct,
  expectedDays,
  requiredHours,
  type LeaveSpan,
  type WorkedException,
} from './contract';

/**
 * Whether this viewer can be shown the ORG-WIDE table.
 *
 * Both, not either: without `people:full` the leave rows are missing and every
 * target is overstated; without `timesheets:full` the hours are missing and every
 * row reads zero. A person's OWN summary needs neither — the self-clauses cover it.
 */
export const canSeeOrgSummary = (perms?: PermMap): boolean =>
  can(perms, 'people', 'full') && can(perms, 'timesheets', 'full');

export type MonthSummary = {
  profileId: string;
  name: string;
  department: string | null;
  /** The person's contracted day, or the org's if they have none. */
  perDay: number;
  /** True when perDay came from the org, i.e. no contract is set for them. */
  usesOrgDay: boolean;
  /** Org workdays minus holidays — their sheet's "Total Working Days". */
  workingDays: number;
  /** Days of PAID leave charged this month (half days count 0.5). */
  leaveDays: number;
  /** Approved UNPAID (LOP) days in the window. Counted, never netted out of the
   *  requirement — that is what makes an LOP day reach payroll as a shortfall. */
  unpaidDays: number;
  /** workingDays − leaveDays. */
  expectedDays: number;
  requiredHours: number;
  /** Prorated to the working days elapsed so far, for a mid-month read. */
  requiredToDate: number;
  workedSeconds: number;
  workedHours: number;
  /** worked ÷ required, uncapped. */
  pct: number;
  /** Worked ÷ WORKING days — their sheet's denominator, so the figures reconcile. */
  avgPerDay: number;
  /** Hours credited under R5 for work that never touched the clock. */
  compHours: number;
  /**
   * worked + credited. The figure the shortfall is actually measured against —
   * paid leave is netted out of `requiredHours` rather than added here, which is
   * the same arithmetic the requirement states as "worked + paid leave + credited".
   */
  effectiveHours: number;
  /** effective − required. Negative is a shortfall. */
  deltaHours: number;
  /** Charged days per leave type id, e.g. { casual: 1, sick: 0.5 }. */
  leaveByType: Record<string, number>;
  halfDays: number;
  overtimeHours: number;
  compOffCredited: number;
  daysPresent: number;
  /** Days whose hours came from a forgotten check-out the sweeper capped. */
  autoClosedDays: number;
};

export type SummaryInput = {
  /**
   * Inclusive org-local ISO bounds. Any range — the month for the summary card,
   * the current week for the Timesheets KPI tiles. Nothing here is month-specific.
   */
  from: string;
  to: string;
  tz: string;
  org: OrgHours;
  workdays: Workdays | null;
  /** Today's org-local key, for the to-date proration. */
  today: string;
};

type ProfileRow = {
  id: string;
  full_name: string | null;
  /** 'active' | 'inactive' (left) | 'archived' (erased). */
  status?: string;
  /** Over the department_id FK — never the legacy text column. */
  departments: { name: string } | null;
  contract_hrs_per_day: number | null;
};
type EntryRow = {
  user_id: string;
  seconds: number | null;
  created_at: string;
  system_closed: boolean | null;
};
type LeaveRow = {
  profile_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  half_day: boolean | null;
};
type ExceptionRow = { profile_id: string; date: string; worked_fraction: number };
type OvertimeRow = {
  profile_id: string;
  ot_seconds: number | null;
  leaves_credited: number | null;
};

const byId = <T>(rows: readonly T[], key: (r: T) => string): Map<string, T[]> => {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const k = key(r);
    const list = m.get(k);
    if (list) list.push(r);
    else m.set(k, [r]);
  }
  return m;
};

/** Widen a date bound by n days, so a ±1 over-fetch can be expressed in ISO. */
const shiftIso = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** A leave type, as the monthly table needs it for one column. `paid` is
 *  leave_types.paid, which defaults to true. */
export type LeaveTypeColumn = { id: string; label: string; paid?: boolean };

/**
 * The org's configured leave types, in display order.
 *
 * The monthly table used to hardcode four columns — Casual, Sick, Annual,
 * Unpaid — so an org with two types got two columns of permanent zeros, and an
 * org with a type of its own never saw it at all. `leaveByType` has always been
 * keyed by the real leave_type_id; only the column list was invented.
 *
 * Deliberately a separate query rather than widening loadSummaries' return
 * type: that has six call sites across four screens, and none of the others
 * render columns.
 */
export const loadLeaveTypes = async (): Promise<LeaveTypeColumn[]> => {
  const sb = supabase;
  if (!sb) return [];
  const { data } = await sb.from('leave_types').select('id, label, paid').order('rank');
  return (data as LeaveTypeColumn[]) ?? [];
};

/**
 * What loadSummaries resolves to: the rows, and `failed` when any of its eight
 * reads errored. A failed read is NOT an empty month and not a month of zeros —
 * with the entries read down, everyone reads as having worked nothing; with the
 * leave read down, every target is overstated. So a failure returns no rows at
 * all, and says so, rather than figures built on the reads that did land
 * (UI-CONVENTIONS §7, TS-14). Still an array, so the surfaces that have not
 * learned to ask render what they rendered for an empty read.
 */
export type SummaryRows = MonthSummary[] & { failed?: true };

/**
 * Where a person stands against the hours that were due BY TODAY, or null once
 * that is the whole month (a closed month, or nothing left to work in it).
 *
 * `deltaHours` measures against the full month, so on day 3 somebody exactly on
 * pace read as 160 hours short, in amber (TS-09). This is a display figure
 * beside it, not a replacement: `deltaHours` and `pct` feed the CSV and the
 * payroll register, and stay what they were.
 */
export const deltaToDate = (
  r: Pick<MonthSummary, 'effectiveHours' | 'requiredHours' | 'requiredToDate'>,
): number | null =>
  r.requiredToDate < r.requiredHours
    ? Math.round((r.effectiveHours - r.requiredToDate) * 100) / 100
    : null;

export const loadSummaries = async (i: SummaryInput): Promise<SummaryRows> => {
  const sb = supabase;
  if (!sb) return [];

  // `created_at` is a timestamptz and the month bounds are org-local dates, so
  // over-fetch a day either side and settle it with orgDateKey — reconstructing
  // an instant from an IANA offset by hand is where these bugs live
  // (docs/UI-CONVENTIONS.md §2).
  const wideFrom = `${shiftIso(i.from, -1)}T00:00:00Z`;
  const wideTo = `${shiftIso(i.to, 2)}T00:00:00Z`;

  const [profiles, entries, leave, exceptions, overtime, holidayRows, types, comp, settled] =
    await Promise.all([
      sb
        .from('profiles')
        .select('id, full_name, status, departments(name), contract_hrs_per_day')
        // Service accounts are not people (20260910100000). They own no hours
        // after the purge, but a row of dashes for a bot is still noise in a
        // table about who worked.
        .neq('status', 'system')
        .order('full_name'),
      sb
        .from('time_entries')
        .select('user_id, seconds, created_at, system_closed')
        .eq('status', 'logged')
        .gte('created_at', wideFrom)
        .lt('created_at', wideTo),
      sb
        .from('leave_requests')
        .select('profile_id, leave_type_id, start_date, end_date, half_day')
        .eq('status', 'approved')
        .lte('start_date', i.to)
        .gte('end_date', i.from),
      sb
        .from('leave_day_exceptions')
        .select('profile_id, date, worked_fraction')
        .gte('date', i.from)
        .lte('date', i.to),
      sb
        .from('overtime_ledger')
        .select('profile_id, ot_seconds, leaves_credited')
        .gte('date', i.from)
        .lte('date', i.to),
      sb.from('holidays').select('date').gte('date', i.from).lte('date', i.to),
      sb.from('leave_types').select('id, paid'),
      // R5. Approved only: a pending claim is an ask, not an hour.
      sb
        .from('compensated_hours')
        .select('profile_id, hours')
        .eq('status', 'approved')
        .gte('date', i.from)
        .lte('date', i.to),
      // Comp off is credited per month when payroll locks
      // (20261008110000); `month` is the first of the month it settles.
      sb
        .from('overtime_settlement')
        .select('profile_id, leaves_credited')
        .gte('month', i.from)
        .lte('month', i.to),
    ]);

  if (
    [profiles, entries, leave, exceptions, overtime, holidayRows, types, comp, settled].some(
      (q) => q.error,
    )
  )
    return Object.assign([], { failed: true as const });

  const paidById = new Map(
    ((types.data as { id: string; paid: boolean }[]) ?? []).map((t) => [t.id, t.paid]),
  );
  const holidays = new Set(((holidayRows.data as { date: string }[]) ?? []).map((h) => h.date));

  const entriesBy = byId((entries.data as EntryRow[]) ?? [], (e) => e.user_id);
  const leaveBy = byId((leave.data as LeaveRow[]) ?? [], (l) => l.profile_id);
  const exceptionsBy = byId((exceptions.data as ExceptionRow[]) ?? [], (e) => e.profile_id);
  const overtimeBy = byId((overtime.data as OvertimeRow[]) ?? [], (o) => o.profile_id);
  // Summed here rather than grouped: nothing downstream needs the individual
  // claims, only the month's credit.
  const compBy = new Map<string, number>();
  for (const c of (comp.data as { profile_id: string; hours: number }[]) ?? []) {
    compBy.set(c.profile_id, (compBy.get(c.profile_id) ?? 0) + (c.hours ?? 0));
  }

  const settledBy = new Map<string, number>();
  for (const o of (settled.data as { profile_id: string; leaves_credited: number }[]) ?? []) {
    settledBy.set(o.profile_id, (settledBy.get(o.profile_id) ?? 0) + Number(o.leaves_credited));
  }

  // Someone who left, or was erased, is listed only for a month they have
  // something in — as payroll_run prices them. Every later month read as a
  // full shortfall for a person nobody expected hours from (bug report
  // 2026-10-07, "deleted user still existing").
  const inMonth = (p: ProfileRow) =>
    (p.status ?? 'active') === 'active' ||
    entriesBy.has(p.id) ||
    leaveBy.has(p.id) ||
    compBy.has(p.id) ||
    overtimeBy.has(p.id);

  return ((profiles.data as unknown as ProfileRow[]) ?? []).filter(inMonth).map((p) => {
    const spans: LeaveSpan[] = (leaveBy.get(p.id) ?? []).map((l) => ({
      start_date: l.start_date,
      end_date: l.end_date,
      half_day: !!l.half_day,
      leave_type_id: l.leave_type_id,
      // An unknown type id defaults to PAID, matching leave_types.paid's own
      // `default true`. Defaulting to unpaid would quietly stop reducing the
      // target the moment someone adds a leave type.
      paid: paidById.get(l.leave_type_id) ?? true,
    }));
    const exceptions: WorkedException[] = (exceptionsBy.get(p.id) ?? []).map((e) => ({
      date: e.date,
      worked_fraction: Number(e.worked_fraction),
    }));

    const charged = chargedLeaveByDay(spans, exceptions, i.from, i.to, i.workdays, holidays);
    // The same expansion over the OTHER kind of span. Deliberately not fed to
    // expectedDays below: unpaid leave keeps its required hours.
    const unpaid = chargedLeaveByDay(
      spans,
      exceptions,
      i.from,
      i.to,
      i.workdays,
      holidays,
      'unpaid',
    );
    const month = expectedDays(i.from, i.to, i.workdays, holidays, charged);
    // Prorate to the elapsed part of the month, but never past its end — on a
    // closed month `today` is later than `to` and the clip keeps it whole.
    const elapsedTo = i.today < i.to ? i.today : i.to;
    const toDate = expectedDays(i.from, elapsedTo, i.workdays, holidays, charged);

    const perDay = personDay(p.contract_hrs_per_day, i.org);

    // Bucket to the ORG day, exactly as stamp_attendance does, then drop the
    // over-fetched edges.
    const dayKeys = new Map<string, { seconds: number; systemClosed: boolean }>();
    for (const e of entriesBy.get(p.id) ?? []) {
      const key = orgDateKey(e.created_at, i.tz);
      if (key < i.from || key > i.to) continue;
      const cur = dayKeys.get(key) ?? { seconds: 0, systemClosed: false };
      cur.seconds += e.seconds ?? 0;
      cur.systemClosed = cur.systemClosed || !!e.system_closed;
      dayKeys.set(key, cur);
    }

    let workedSeconds = 0;
    let daysPresent = 0;
    let autoClosedDays = 0;
    for (const d of dayKeys.values()) {
      workedSeconds += d.seconds;
      if (d.seconds > 0) daysPresent += 1;
      if (d.systemClosed) autoClosedDays += 1;
    }

    const leaveByType: Record<string, number> = {};
    let halfDays = 0;
    // Attribute each charged day to the FIRST span covering it, so overlapping
    // spans cannot double-count — chargedLeaveByDay already capped the day at 1,
    // and the server likewise settles a day against exactly one request.
    for (const [day, amount] of charged) {
      if (amount === 0) continue;
      const owner = spans.find((s) => s.paid && day >= s.start_date && day <= s.end_date);
      if (!owner) continue;
      leaveByType[owner.leave_type_id] = (leaveByType[owner.leave_type_id] ?? 0) + amount;
      if (amount === 0.5) halfDays += 1;
    }
    // And the unpaid ones, into the same map, so it holds every type's days.
    // The table lists only paid types by name: unpaid leave is its own
    // column, `unpaidDays`, and a per-type row repeated it (MonthlySummary).
    let unpaidDays = 0;
    for (const [day, amount] of unpaid) {
      if (amount === 0) continue;
      unpaidDays += amount;
      const owner = spans.find((s) => !s.paid && day >= s.start_date && day <= s.end_date);
      if (!owner) continue;
      leaveByType[owner.leave_type_id] = (leaveByType[owner.leave_type_id] ?? 0) + amount;
    }

    const required = requiredHours(perDay, month.expected);
    const workedHours = Math.round((workedSeconds / 3600) * 100) / 100;
    const compHours = Math.round((compBy.get(p.id) ?? 0) * 100) / 100;
    // Rounded to 2dp BEFORE the delta, matching how workedHours already feeds it —
    // so a payslip and this table cannot differ by a rounding step.
    const effectiveHours = Math.round((workedHours + compHours) * 100) / 100;
    const ot = overtimeBy.get(p.id) ?? [];

    return {
      profileId: p.id,
      name: p.full_name ?? '—',
      department: p.departments?.name ?? null,
      perDay,
      usesOrgDay: !(p.contract_hrs_per_day != null && p.contract_hrs_per_day > 0),
      workingDays: month.workingDays,
      leaveDays: month.leaveDays,
      unpaidDays: Math.round(unpaidDays * 100) / 100,
      expectedDays: month.expected,
      requiredHours: required,
      requiredToDate: requiredHours(perDay, toDate.expected),
      workedSeconds,
      workedHours,
      compHours,
      effectiveHours,
      pct: completionPct(effectiveHours, required),
      avgPerDay: month.workingDays ? Math.round((workedHours / month.workingDays) * 100) / 100 : 0,
      deltaHours: Math.round((effectiveHours - required) * 100) / 100,
      leaveByType,
      halfDays,
      overtimeHours:
        Math.round((ot.reduce((s, o) => s + (o.ot_seconds ?? 0), 0) / 3600) * 100) / 100,
      // Legacy per-day credits (before Sep 2026) plus the month's settlement.
      compOffCredited:
        ot.reduce((s, o) => s + (o.leaves_credited ?? 0), 0) + (settledBy.get(p.id) ?? 0),
      daysPresent,
      autoClosedDays,
    };
  });
};
